// JQ4-42 · US-AUDIT-002 — shared audit-action label map + actor resolution.
//
// Single source of truth for rendering ticket audit-log entries. Consumed by
// BOTH TicketHistoryPage (full audit trail, JQ4-42) and TicketDetailPage
// (embedded audit-timeline section, admin-analytics-expansion D7) so the two
// surfaces never drift — extracted (P3b review-fix) after TicketDetailPage
// was found importing ACTION_LABELS directly from the TicketHistoryPage
// *page* module (a page-to-page import smell) and missing the
// 'ticket.reopened' entry (JQ4-28, an active wired action per
// shared/audit/ticket-audit-actions.ts).

import { useTranslation } from '@jq4race/i18n';
import type { AuditLogEntryResponse } from '@jq4race/shared';

import { isValidUuid } from '../../audit-log/lib/isValidUuid';
import { resolveActionLabel as resolveAuditActionLabel } from '../../audit-log/lib/resolveSummary';

/**
 * Static English source-of-truth for known ticket audit actions (JQ4-42 REQ-9).
 * Kept as a plain object (not translated in place) because
 * TicketHistoryPage.test.tsx imports it directly outside of any i18n render
 * tree to assert the action-label contract; it also doubles as the
 * `defaultValue` fallback for the translated `useActionLabel()` hook below.
 */
export const ACTION_LABELS: Record<string, string> = {
  'ticket.created': 'Created',
  'ticket.retrieval_requested': 'Retrieval requested',
  'ticket.retrieval_claimed': 'Retrieval claimed',
  'ticket.delivered': 'Delivered',
  'ticket.sms_sent': 'SMS sent',
  'ticket.sms_failed': 'SMS failed',
  'ticket.reopened': 'Reopened',
};

export function resolveActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

/** Sentinel to tell "no ticket-specific label" apart from a real one. */
const NO_LABEL = '__jq4_no_ticket_history_label__';

/**
 * Translated (t()-driven) action-label resolver — namespace 'ticket-history'.
 * Catalog key `actionLabels.<action>` mirrors the raw action string itself
 * (e.g. `actionLabels.ticket.created`), so the JSON nesting doubles as the
 * lookup path with no separate key-mapping table to keep in sync.
 *
 * Actions without a short ticket-specific label (payment.*, sms.resent, ...)
 * resolve through the full audit-log catalogue instead of showing the raw code.
 */
export function useActionLabel(): (action: string) => string {
  const { t } = useTranslation('ticket-history');
  return (action: string) => {
    const specific = t(`actionLabels.${action}`, { defaultValue: NO_LABEL });
    if (specific !== NO_LABEL) return specific;
    return resolveAuditActionLabel(action, t);
  };
}

export type ActorSource = Pick<
  AuditLogEntryResponse,
  'actorUserId' | 'actorEmail' | 'actorRole' | 'metadata'
>;

/**
 * Who did it, for a ticket-timeline row (QA R2-11 — it used to print the actor
 * UUID, because the backend writes `metadata.actorName = actor.sub`).
 *
 *  - station principal    -> "Station · <label>" (even when actorUserId is null)
 *  - actorUserId === null -> translated 'visitor' (REQ-9 Scenario 9b)
 *  - otherwise the resolved identity: actorEmail, else a NON-uuid
 *    metadata.actorName, else a short id — followed by the translated role.
 */
export function useResolveActor(): (item: ActorSource) => string {
  const { t } = useTranslation('ticket-history');
  return (item: ActorSource) => {
    const metadata = (item.metadata ?? {}) as Record<string, unknown>;
    // A station principal has no identity_users row, so its actorUserId may be
    // null: check it BEFORE the null-actor (visitor) branch.
    if (metadata.principalType === 'station' && typeof metadata.stationLabel === 'string') {
      return `${t('actor.station')} · ${metadata.stationLabel}`;
    }
    if (item.actorUserId === null) return t('actor.visitor');

    const name =
      typeof metadata.actorName === 'string' &&
      metadata.actorName.trim() !== '' &&
      !isValidUuid(metadata.actorName)
        ? metadata.actorName
        : null;
    const who = item.actorEmail ?? name ?? `${item.actorUserId.slice(0, 8)}…`;
    const role = item.actorRole
      ? t(`audit-log:roles.${item.actorRole}`, { defaultValue: item.actorRole })
      : null;
    return role ? `${who} · ${role}` : who;
  };
}
